/**
 * Shapes of the two Search-flow endpoints (admin_search_flow.py):
 *   GET  search/flow_graph  -> FlowGraphData   (the live pipeline as a graph)
 *   POST search/flow_trace  -> FlowTrace       (one real run, step by step)
 *
 * Everything is typed loosely where the server may grow a field or send a
 * string where a list is documented: the tab must render whatever arrives
 * rather than throw on a shape it did not expect.
 */

export const LANES = ['request', 'understand', 'route', 'retrieve', 'rank', 'respond'] as const;
export const LANE_LABELS: Record<string, string> = {
  request: 'Request', understand: 'Understand', route: 'Route',
  retrieve: 'Retrieve', rank: 'Rank', respond: 'Respond',
};

export type NodeKind = 'step' | 'decision' | 'parallel' | 'store' | 'external';
export type ParamSource = 'setting' | 'env' | 'code' | 'live';
export type StepStatus = 'ran' | 'skipped' | 'cached' | 'degraded' | 'failed';

export interface FlowParam {
  key: string;
  label?: string;
  value: any;
  unit?: string;
  source?: ParamSource | string;
  note?: string;
}

export interface FlowExample {
  input?: any;
  output?: any;
  note?: string;
  source?: string;
}

export interface FlowSubstep {
  id: string;
  label: string;
  summary?: string;
  description?: string;
  code?: string;
  params?: FlowParam[];
  example?: FlowExample | null;
}

export interface FlowNode {
  id: string;
  label: string;
  lane: string;
  order: number;
  kind: NodeKind | string;
  summary?: string;
  description?: string;
  code?: string;
  params?: FlowParam[];
  substeps?: FlowSubstep[];
  example?: FlowExample | null;
}

export interface FlowEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
  condition?: string;
  kind?: 'normal' | 'fallback' | 'loop' | string;
}

export interface ScenarioExample {
  query?: string;
  filters?: Record<string, any> | null;
  force_route?: string | null;
  as?: string;
  use_cache?: boolean;
}

/** The most recent matching production run (search_query_logs, read-only). */
export interface ScenarioRealRun {
  date?: string;
  backend?: string;
  query?: string;
  filters?: Record<string, any> | null;
  ai_filters?: Record<string, any> | null;
  ai_search_query?: string | null;
  total_results?: number | null;
  total_ms?: number | null;
  timings?: Record<string, number | null> | null;
  rescore?: { model?: string; scored?: number; biggest_moves?: any[] } | null;
  top?: { rank?: number; title?: string; percentage?: number | null; [k: string]: any }[];
  [k: string]: any;
}

export interface FlowScenario {
  id: string;
  label: string;
  description?: string;
  example?: ScenarioExample | null;
  path: string[];
  edges?: string[];
  notes?: string | string[];
  real_run?: ScenarioRealRun | null;
}

export interface FlowLive {
  engine?: string;
  simple_route?: string;
  typesense?: { up?: boolean; docs?: number | null; error?: string | null } | null;
  pinecone?: { index?: string; namespace?: string | null } | null;
  rescore_model?: string;
  corpus_count?: number | null;
  settings?: Record<string, any>;
  [k: string]: any;
}

export interface FlowGraphData {
  nodes: FlowNode[];
  edges: FlowEdge[];
  scenarios: FlowScenario[];
  live?: FlowLive | null;
  generated_at?: string;
}

export interface TraceStep {
  node_id: string;
  substep_id?: string | null;
  status: StepStatus | string;
  t_start_ms?: number | null;
  ms?: number | null;
  input?: any;
  output?: any;
  notes?: string[] | string | null;
}

export interface TraceResult {
  rank?: number;
  title?: string;
  university?: string;
  country?: string;
  trl?: number | string | null;
  percentage?: number | null;
  score_rule?: string;
  legs?: string[] | Record<string, any> | string | null;
  llm_reason?: string | null;
  llm_band?: string | null;
  /** true: on the traced page (page 1, the first hits_per_page rows); false: a later page.
   *  Rows below min_relevance_deep never reach `results`, so this says nothing about relevance. */
  shown?: boolean;
  [k: string]: any;
}

export interface FlowTrace {
  path: string[];
  edges: string[];
  route?: string;
  engine?: string;
  scenario_guess?: string | null;
  steps: TraceStep[];
  results: TraceResult[];
  totals?: { total?: number; total_uncapped?: number };
  timings?: Record<string, any>;
  degraded?: string[] | null;
  meta?: any;
  took_ms?: number;
  /** false when the pipeline itself answered with an error envelope */
  success?: boolean;
  error?: string | null;
  message?: string | null;
  notes?: string[] | string | null;
  /** what the server actually ran: the request echo and the entitlement it simulated */
  request?: any;
  entitlements?: any;
}

/** What the form posted, kept beside the answer so the header can say it. */
export interface TraceRequest {
  query: string;
  filters: Record<string, any>;
  force_route: 'engine' | null;
  as: 'paid' | 'free' | 'anonymous';
  use_cache: boolean;
}

export const asList = (v: any): string[] => {
  if (v === null || v === undefined || v === '') return [];
  if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : JSON.stringify(x)));
  return [String(v)];
};

export const num = (v: any): number | null => {
  if (v === null || v === undefined || v === '' || typeof v === 'boolean') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const fmtMs = (v: any): string => {
  const n = num(v);
  if (n === null) return '—';
  if (n >= 10000) return `${(n / 1000).toFixed(1)} s`;
  if (n > 0 && n < 1) return '<1 ms';
  if (n > 0 && n < 10 && !Number.isInteger(n)) return `${n.toFixed(1)} ms`;
  return `${Math.round(n).toLocaleString()} ms`;
};

/** Status colours used by the graph, the panel and the timeline alike. */
export const STATUS_STYLE: Record<string, { fill: string; stroke: string; text: string; label: string; cls: string }> = {
  ran:      { fill: '#E9F6EE', stroke: '#2F9E5B', text: '#1F6B3D', label: 'ran',      cls: 'bg-green-50 text-green-700 border-green-300' },
  skipped:  { fill: '#F2F2F2', stroke: '#9A9A9A', text: '#525252', label: 'skipped',  cls: 'bg-grayscale-200 text-grayscale-1100 border-grayscale-400' },
  cached:   { fill: '#E7F0FB', stroke: '#2C6FC4', text: '#1D4E8F', label: 'cached',   cls: 'bg-blue-50 text-blue-700 border-blue-300' },
  degraded: { fill: '#FFF4E0', stroke: '#D08A1E', text: '#8A5A0C', label: 'degraded', cls: 'bg-amber-50 text-amber-800 border-amber-300' },
  failed:   { fill: '#FDECEC', stroke: '#D0443A', text: '#9B2A22', label: 'failed',   cls: 'bg-red-50 text-red-700 border-red-300' },
};
export const statusStyle = (s: string | undefined | null) => STATUS_STYLE[String(s || '')] || STATUS_STYLE.ran;
